/**
 * Ready Panel Component
 *
 * Streamlined overlay showing opponent status, timer, and ready button.
 * Positioned at bottom-right of the deployment grid.
 */

import React from 'react';
import './ReadyPanel.css';

export interface ReadyPanelProps {
  /** Current player ID */
  currentPlayerId: 'player1' | 'player2';
  /** Player 1 ready state */
  player1Ready: boolean;
  /** Player 2 ready state */
  player2Ready: boolean;
  /** Whether both players are locked */
  isLocked: boolean;
  /** Countdown seconds (null if not active) */
  countdownSeconds: number | null;
  /** Whether current player can mark ready */
  canMarkReady: boolean;
  /** Callback when ready button clicked */
  onReady: () => void;
  /** Callback when unready button clicked */
  onUnready: () => void;
}

export const ReadyPanel: React.FC<ReadyPanelProps> = ({
  currentPlayerId,
  player1Ready,
  player2Ready,
  isLocked,
  countdownSeconds,
  canMarkReady,
  onReady,
  onUnready
}) => {
  const currentPlayerReady = currentPlayerId === 'player1' ? player1Ready : player2Ready;
  const opponentReady = currentPlayerId === 'player1' ? player2Ready : player1Ready;

  // Determine panel state for styling
  const getPanelClass = () => {
    if (isLocked) return 'ready-panel locked';
    if (countdownSeconds !== null && countdownSeconds <= 10) return 'ready-panel urgent';
    if (countdownSeconds !== null) return 'ready-panel countdown';
    return 'ready-panel';
  };

  return (
    <div className={getPanelClass()}>
      {/* Locked State */}
      {isLocked ? (
        <div className="locked-message">
          <span className="locked-text">Combat Starting...</span>
        </div>
      ) : (
        <>
          {/* Opponent Status */}
          <div className="opponent-status">
            <span className="status-label">Opponent:</span>
            <span className={`status-value ${opponentReady ? 'ready' : 'not-ready'}`}>
              {opponentReady ? 'Ready' : 'Not Ready'}
            </span>
          </div>

          {/* Timer - only show when countdown active */}
          {countdownSeconds !== null && (
            <div className={`timer ${countdownSeconds <= 10 ? 'urgent' : ''}`}>
              <span className="timer-value">{countdownSeconds}</span>
              <span className="timer-label">s</span>
            </div>
          )}

          {/* Ready Button */}
          <div className="ready-action">
            {currentPlayerReady ? (
              <button
                onClick={onUnready}
                className="ready-button cancel"
              >
                Cancel
              </button>
            ) : (
              <button
                onClick={onReady}
                disabled={!canMarkReady}
                className={`ready-button ${canMarkReady ? 'active' : 'disabled'}`}
              >
                {canMarkReady ? 'Ready' : 'Place creatures'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
